import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Send, FileText, Film, Music, Plus, Trash2, Mic, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/* Envio de arquivos e mensagens de voz no Atendimento, no estilo do WhatsApp Web:
 *  - AnexosModal: prévia dos arquivos escolhidos (clipe, arrastar ou colar), legenda por arquivo, enviar todos;
 *  - useGravador + BarraGravacao: grava pelo microfone e envia como mensagem de voz. */

export const MAX_MB = 50;
export const tipoDe = (f) => {
  const t = (f?.type || '').toLowerCase();
  if (/^image\/(jpeg|png|webp)$/.test(t)) return 'image';
  if (/^video\//.test(t)) return 'video';
  if (/^audio\//.test(t)) return 'audio';
  return 'document';
};
const tamanho = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
export const tempo = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/** Filtra o que dá para enviar (tamanho) e avisa o resto. */
export function aceitarArquivos(lista) {
  const ok = [];
  for (const f of Array.from(lista || [])) {
    if (!f || !f.size) continue;
    if (f.size > MAX_MB * 1048576) { toast.error(`${f.name}: maior que ${MAX_MB} MB`); continue; }
    ok.push(f);
  }
  return ok;
}

function Miniatura({ arquivo, url, grande }) {
  const t = tipoDe(arquivo);
  const cls = grande ? 'max-h-[46vh] max-w-full rounded-lg' : 'w-full h-full object-cover';
  if (t === 'image') return <img src={url} alt={arquivo.name} className={cls} />;
  if (t === 'video') return grande ? <video src={url} controls className={cls} /> : <div className="w-full h-full flex items-center justify-center bg-slate-800 text-white"><Film className="w-5 h-5" /></div>;
  const Icone = t === 'audio' ? Music : FileText;
  const ext = (arquivo.name.split('.').pop() || '').slice(0, 4).toUpperCase();
  if (!grande) return <div className="w-full h-full flex flex-col items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-500"><Icone className="w-5 h-5" /><span className="text-[9px] font-bold">{ext}</span></div>;
  return (
    <div className="flex flex-col items-center gap-3 py-8">
      <div className="w-20 h-24 rounded-lg bg-red-500 text-white flex items-center justify-center text-sm font-bold shadow">{ext || <Icone className="w-8 h-8" />}</div>
      <p className="text-sm font-medium text-center break-all max-w-sm">{arquivo.name}</p>
      <p className="text-xs text-slate-500">{tamanho(arquivo.size)}</p>
      {t === 'audio' && <audio src={url} controls className="w-72" />}
    </div>
  );
}

export function AnexosModal({ arquivos, onMais, onRemover, onClose, onEnviar, destino }) {
  const [atual, setAtual] = useState(0);
  const [legendas, setLegendas] = useState({});
  const inputMais = useRef(null);
  const urls = useMemo(() => arquivos.map(f => URL.createObjectURL(f)), [arquivos]);
  useEffect(() => () => urls.forEach(u => URL.revokeObjectURL(u)), [urls]);
  useEffect(() => { if (atual >= arquivos.length) setAtual(Math.max(0, arquivos.length - 1)); }, [arquivos.length, atual]);
  const f = arquivos[atual];
  const chave = (x) => `${x.name}-${x.size}-${x.lastModified}`;
  const enviar = () => onEnviar(arquivos.map(x => ({ arquivo: x, legenda: (legendas[chave(x)] || '').trim() })));
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  if (!f) return null;
  const semLegenda = tipoDe(f) === 'audio';
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[94vh]" onClick={e => e.stopPropagation()}
        role="dialog" aria-label="Enviar arquivos" data-testid="wa-anexos-modal">
        <div className="px-4 py-3 border-b dark:border-slate-800 flex items-center gap-3">
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200" aria-label="Cancelar"><X className="w-5 h-5" /></button>
          <p className="text-sm font-medium truncate flex-1">{f.name} <span className="text-slate-400 font-normal">· {tamanho(f.size)}</span></p>
          {destino && <span className="text-xs text-slate-500 truncate max-w-[40%]">para {destino}</span>}
        </div>
        <div className="flex-1 min-h-0 overflow-auto bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
          <Miniatura arquivo={f} url={urls[atual]} grande />
        </div>
        <div className="px-4 pt-3">
          {semLegenda ? (
            <p className="text-xs text-slate-500 py-2">Áudio vai sem legenda (o WhatsApp não mostra legenda em áudio).</p>
          ) : (
            <input value={legendas[chave(f)] || ''} onChange={e => setLegendas(l => ({ ...l, [chave(f)]: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
              placeholder="Legenda (opcional)" autoFocus data-testid="wa-anexo-legenda"
              className="w-full h-10 px-4 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30" />
          )}
        </div>
        <div className="px-4 py-3 flex items-center gap-2">
          <div className="flex gap-2 overflow-x-auto flex-1 py-1">
            {arquivos.map((x, i) => (
              <div key={chave(x)} className={`relative w-14 h-14 rounded-lg overflow-hidden shrink-0 cursor-pointer ring-2 ${i === atual ? 'ring-emerald-500' : 'ring-transparent'}`}
                onClick={() => setAtual(i)} title={x.name} data-testid={`wa-anexo-mini-${i}`}>
                <Miniatura arquivo={x} url={urls[i]} />
                <button onClick={(e) => { e.stopPropagation(); onRemover(i); }} aria-label={`Remover ${x.name}`}
                  className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
              </div>
            ))}
            <button onClick={() => inputMais.current?.click()} title="Adicionar arquivo" aria-label="Adicionar arquivo"
              className="w-14 h-14 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 text-slate-400 flex items-center justify-center shrink-0 hover:text-slate-600">
              <Plus className="w-5 h-5" />
            </button>
            <input ref={inputMais} type="file" multiple hidden onChange={e => { onMais(e.target.files); e.target.value = ''; }} />
          </div>
          <button onClick={enviar} data-testid="wa-anexos-enviar" aria-label={`Enviar ${arquivos.length} arquivo(s)`}
            className="h-12 w-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center shadow-lg shrink-0 relative">
            <Send className="w-5 h-5" />
            {arquivos.length > 1 && <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-white text-emerald-700 text-[11px] font-bold flex items-center justify-center shadow">{arquivos.length}</span>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- gravação de voz */
const TIPOS_GRAVACAO = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
const MAX_SEG = 10 * 60;

export function useGravador(onPronto) {   // onPronto(file, duracaoSeg) ao enviar
  const [estado, setEstado] = useState('parado');   // parado | pedindo | gravando
  const [seg, setSeg] = useState(0);
  const [nivel, setNivel] = useState(0);
  const rec = useRef(null); const partes = useRef([]); const stream = useRef(null);
  const tick = useRef(null); const inicio = useRef(0); const fimCb = useRef(null); const analise = useRef(null);
  const prontoRef = useRef(onPronto); prontoRef.current = onPronto;

  const limpar = () => {
    clearInterval(tick.current); tick.current = null;
    try { stream.current?.getTracks().forEach(t => t.stop()); } catch (_) { /* noop */ }
    try { analise.current?.ctx?.close(); } catch (_) { /* noop */ }
    stream.current = null; rec.current = null; analise.current = null;
    setEstado('parado'); setSeg(0); setNivel(0);
  };
  useEffect(() => limpar, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const iniciar = async () => {
    if (estado !== 'parado') return;
    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === 'undefined') {
      toast.error('Este navegador não grava áudio'); return;
    }
    setEstado('pedindo');
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch (e) {
      setEstado('parado');
      toast.error(e?.name === 'NotAllowedError' ? 'Permita o uso do microfone no navegador para gravar' : 'Não foi possível acessar o microfone');
      return;
    }
    const tipo = TIPOS_GRAVACAO.find(t => window.MediaRecorder.isTypeSupported?.(t)) || '';
    const r = new window.MediaRecorder(stream.current, tipo ? { mimeType: tipo, audioBitsPerSecond: 32000 } : undefined);
    partes.current = [];
    r.ondataavailable = (e) => { if (e.data?.size) partes.current.push(e.data); };
    r.onstop = () => {
      const mime = (r.mimeType || tipo || 'audio/webm').split(';')[0];
      const blob = new Blob(partes.current, { type: r.mimeType || tipo || 'audio/webm' });
      const dur = (Date.now() - inicio.current) / 1000;
      const cb = fimCb.current; fimCb.current = null;
      limpar();
      if (cb && blob.size) {
        const ext = mime.includes('ogg') ? 'ogg' : mime.includes('mp4') ? 'm4a' : 'webm';
        cb(new File([blob], `audio-${new Date().toISOString().replace(/[:.]/g, '-')}.${ext}`, { type: blob.type }), dur);
      }
    };
    rec.current = r;
    try {   // medidor de volume (só visual)
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream.current); const an = ctx.createAnalyser(); an.fftSize = 256;
      src.connect(an); analise.current = { ctx, an, buf: new Uint8Array(an.frequencyBinCount) };
    } catch (_) { /* sem medidor */ }
    inicio.current = Date.now();
    r.start(1000);
    setEstado('gravando');
    tick.current = setInterval(() => {
      const s = (Date.now() - inicio.current) / 1000;
      setSeg(s);
      const a = analise.current;
      if (a) { a.an.getByteFrequencyData(a.buf); setNivel(a.buf.reduce((x, y) => x + y, 0) / a.buf.length / 255); }
      if (s >= MAX_SEG) { toast.info('Limite de 10 minutos: enviando'); parar(true); }
    }, 200);
  };
  // para; enviar=true entrega o arquivo para onPronto(file, duracao), senão descarta
  const parar = (enviar) => {
    if (!rec.current) { limpar(); return; }
    fimCb.current = enviar ? (...a) => prontoRef.current?.(...a) : null;
    try { rec.current.state !== 'inactive' ? rec.current.stop() : limpar(); } catch (_) { limpar(); }
  };
  return { estado, seg, nivel, iniciar, enviar: () => parar(true), cancelar: () => parar(false) };
}

export function BarraGravacao({ gravador }) {
  return (
    <div className="flex-1 min-w-0 flex items-center gap-3 h-10 px-2" data-testid="wa-gravando">
      <button onClick={gravador.cancelar} title="Descartar" aria-label="Descartar gravação" data-testid="wa-gravacao-cancelar"
        className="h-10 w-10 rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center shrink-0">
        <Trash2 className="w-5 h-5" />
      </button>
      <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse shrink-0" />
      <span className="text-sm tabular-nums text-slate-700 dark:text-slate-200 w-12 shrink-0" data-testid="wa-gravacao-tempo">{tempo(gravador.seg)}</span>
      <div className="flex-1 flex items-center gap-[3px] h-6 overflow-hidden" aria-hidden="true">
        {Array.from({ length: 36 }).map((_, i) => {
          const h = Math.max(0.12, Math.min(1, gravador.nivel * 2.2 * (0.55 + 0.45 * Math.abs(Math.sin(i * 1.7 + gravador.seg * 6)))));
          return <span key={i} className="w-[3px] rounded-full bg-emerald-500/70" style={{ height: `${h * 100}%` }} />;
        })}
      </div>
      <button onClick={gravador.enviar} title="Enviar mensagem de voz" aria-label="Enviar mensagem de voz" data-testid="wa-gravacao-enviar"
        className="h-10 w-10 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center shadow shrink-0">
        <Send className="w-4 h-4" />
      </button>
    </div>
  );
}

export function BotaoMic({ gravador, disabled }) {
  const pedindo = gravador.estado === 'pedindo';
  return (
    <button onClick={gravador.iniciar} disabled={disabled || pedindo} title="Gravar mensagem de voz" aria-label="Gravar mensagem de voz" data-testid="wa-mic-btn"
      className="h-10 w-10 shrink-0 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white flex items-center justify-center shadow">
      {pedindo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-[18px] h-[18px]" />}
    </button>
  );
}

